import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { FormWrapper } from 'virtues-bootstrap';
import { mapIndex } from 'utilities/ramda';
import CardiovascularInvestigationsInReport from 'containers/CardiovascularInvestigationsInReport';
import AdaptiveDataRow from 'components/DataBlock/AdaptiveDataRow';

const PatientHistory = ({
  birthDate,
  cardiacRiskFactors,
  pacemakerDependent,
  ejectionFractionMostRecent,
  deviceType,
  heartDisease,
  icdInd,
  nyhaClass,
  sex,
  priorArtialArrhythmia,
  priorTreatmentDetection,
  treatment,
  priorVentricularArrhythmia,
  medications,
  frailtyScore,
  hospitalizations,
  baselineBloodwork,
  t,
  treatmentType,
  AntiarrhythmicMedication,
}) => (
  <FormWrapper title={t('patient.patientHistory')}>
    <AdaptiveDataRow title={t('reports.personal')}>
      {birthDate} {sex}, {deviceType}
    </AdaptiveDataRow>
    <AdaptiveDataRow title={t('reports.pacemakerDependent')}>{pacemakerDependent}</AdaptiveDataRow>
    <AdaptiveDataRow title={t('reports.indicationForImplant')}>{icdInd}</AdaptiveDataRow>
    <AdaptiveDataRow title={t('reports.targetedMedicalHistory')}>{heartDisease}</AdaptiveDataRow>
    <AdaptiveDataRow title={t('reports.priorVentricularArrhythmia')}>
      {priorVentricularArrhythmia}
    </AdaptiveDataRow>
    <AdaptiveDataRow title={t('reports.priorTreatmentDetection')}>
      {priorTreatmentDetection}
    </AdaptiveDataRow>
    <AdaptiveDataRow title={t('reports.treatmentDetectionByDevice')}>
      {treatmentType}
    </AdaptiveDataRow>
    <AdaptiveDataRow title={t('reports.priorOrCurrentTreatmentForVentricularArrhythmia')}>
      {treatment}
    </AdaptiveDataRow>
    <AdaptiveDataRow title={t('reports.isThePatientOnAntiArrhythmiaMed')}>
      {AntiarrhythmicMedication}
    </AdaptiveDataRow>
    <AdaptiveDataRow title={t('reports.priorArtialArrhythmia')}>
      {priorArtialArrhythmia}
    </AdaptiveDataRow>
    <AdaptiveDataRow title={t('reports.nyhaClass')}>{nyhaClass}</AdaptiveDataRow>
    <AdaptiveDataRow title={t('reports.cardiacRiskFactors')}>{cardiacRiskFactors}</AdaptiveDataRow>
    <AdaptiveDataRow title={t('reports.ejectionFractionMostRecent')}>
      {ejectionFractionMostRecent}
    </AdaptiveDataRow>
    <AdaptiveDataRow title={t('reports.medications')}>
      {mapIndex(
        ({ name, dose, unit, freq }, index) => (
          <div key={index}>{`${name}, ${dose}${unit}, ${freq}`}</div>
        ),
        medications,
      )}
    </AdaptiveDataRow>
    <AdaptiveDataRow title={t('medicalInformation.cardiovascularInvestigations')}>
      <CardiovascularInvestigationsInReport />
    </AdaptiveDataRow>
    <AdaptiveDataRow title={t('medicalInformation.frailtyScore')}>
      {mapIndex(
        ({ value }, index) => (
          <div key={index}>{value}</div>
        ),
        frailtyScore,
      )}
    </AdaptiveDataRow>
    <AdaptiveDataRow title={t('medicalInformation.previousCardiovascularHospitalizations')}>
      {mapIndex(
        ({ title, value }, index) => (
          <div key={index}>
            {title} - {value}
          </div>
        ),
        hospitalizations,
      )}
    </AdaptiveDataRow>
    <AdaptiveDataRow title={t('medicalInformation.baselineBloodwork')}>
      {mapIndex(
        ({ title, date, value }, index) => (
          <div key={index}>
            {date} - {title}: {value}
          </div>
        ),
        baselineBloodwork,
      )}
    </AdaptiveDataRow>
  </FormWrapper>
);

PatientHistory.propTypes = {
  birthDate: PropTypes.string.isRequired,
  cardiacRiskFactors: PropTypes.string.isRequired,
  pacemakerDependent: PropTypes.string.isRequired,
  ejectionFractionMostRecent: PropTypes.string.isRequired,
  deviceType: PropTypes.string.isRequired,
  heartDisease: PropTypes.string.isRequired,
  icdInd: PropTypes.string.isRequired,
  nyhaClass: PropTypes.string.isRequired,
  sex: PropTypes.string.isRequired,
  priorArtialArrhythmia: PropTypes.string.isRequired,
  priorVentricularArrhythmia: PropTypes.string.isRequired,
  priorTreatmentDetection: PropTypes.string.isRequired,
  treatmentType: PropTypes.string.isRequired,
  AntiarrhythmicMedication: PropTypes.string.isRequired,
  treatment: PropTypes.string.isRequired,
  medications: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      dose: PropTypes.string,
      unit: PropTypes.string,
      freq: PropTypes.string,
    }),
  ).isRequired,
  frailtyScore: PropTypes.arrayOf(PropTypes.object).isRequired,
  hospitalizations: PropTypes.arrayOf(PropTypes.object).isRequired,
  baselineBloodwork: PropTypes.arrayOf(PropTypes.object).isRequired,
  t: PropTypes.func.isRequired,
};

export default withTranslation()(PatientHistory);
